import { Document, Page, Text, View, StyleSheet, Link, Image } from "@react-pdf/renderer";

const cleanUrl = (url) => {
  return url ? url.replace(/^(https?:\/\/)?(www\.)?/i, '') : '';
};

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 40,
    fontFamily: "Times-Roman",
  },
  section: {
    marginBottom: 10,
  },
  header: {
    fontSize: 14,
    marginBottom: 8,
    textAlign: "center",
    fontWeight: 'extrabold'
  },
  headerProfile: {
    fontSize: 16,
    marginBottom: 8,
    fontWeight: 'extrabold'
  },
  subheader: {
    fontSize: 12,
    marginBottom: 10,
    borderBottom: 1,
    borderBottomColor: "#000000",
    fontWeight: 'extrabold'
  },
  text: {
    fontSize: 11,
    marginBottom: 4,
  },
  boldText: {
    fontSize: 11,
    fontWeight: 'extrabold',
    marginBottom: 4,
    textTransform: 'capitalize',
  },
  title: {
    fontSize: 12,
    fontWeight: 'extrabold',
    marginBottom: 4,
  },
  companyName: {
    fontSize: 12,
    fontWeight: 'extrabold',
    marginBottom: 3,
  },
  institutionName: {
    fontSize: 12,
    fontWeight: 'extrabold',
    marginBottom: 3,
  },
  listItem: {
    marginLeft: 15,
    marginBottom: 4,
  },
  portfolioLink: {
    fontSize: 11,
    marginBottom: 4,
    color: '#0000EE',
    textDecoration: 'underline',
  },
  achievementDate: {
    fontSize: 11,
    fontStyle: 'italic',
    marginBottom: 2,
  },
  avatarContainer: {
    width: '25%',
    marginLeft: 5,
    marginRight: 10,
    overflow: 'hidden', // Tambahkan ini
  },
  avatar: {
    width: 90,
    height: 120,
    borderRadius: 4,
    objectFit: 'cover', // Tambahkan properti ini
  },
  headerContent: {
    width: '100%',
    justifyContent:"flex-start"
  },
});

const formatDateRange = (start, end) => {
  const format = (dateStr) => {
    if (!dateStr || dateStr.toLowerCase() === 'now') return 'Now';
    const date = new Date(dateStr.includes('-') ? dateStr : `${dateStr}-01`);
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long' }).format(date);
  };

  const formattedStart = format(start);
  const formattedEnd = format(end);

  return `(${formattedStart}${end ? ` - ${formattedEnd}` : ' - Now'})`;
};

const getYearRange = (start, end) => {
  const startYear = start?.substring(0, 4);
  const endYear = end?.substring(0, 4);
  
  if (!startYear) return '';
  if (!endYear) return startYear;
  
  return `${startYear} - ${endYear}`;
};

const formatSingleDate = (dateInput) => {
  if (!dateInput) return null;
  const dateStr = dateInput.match(/^\((.*?)\)$/)?.[1] || dateInput;
  
  const format = (date) => {
    try {
      const dateObj = new Date(date.includes('-') ? date : `${date}-01`);
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long' }).format(dateObj);
    } catch {
      return date; 
    }
  };
  return `(${format(dateStr)})`;
};

const BasicAtsTemplate = ({
  profileData = {},
  experienceData = [],
  educationData = [],
  certificateData = [],
  publicationData = [],
  skillData = [],
  achievementData = [],
  portfolioData = [],
}) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Profile Section */}
        <View style={styles.section}>
          {profileData?.show_avatar && profileData?.avatarUrl ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
            <View style={styles.avatarContainer}>
              <Image style={styles.avatar} src={profileData.avatarUrl}/>
            </View>
            <View style={styles.headerContent}>
              <Text style={styles.headerProfile}>{profileData?.fullname || "Fill The Data"}</Text>
              <View style={{ flexDirection: 'row' }}>
                <Text style={styles.title}>{profileData.title}</Text>
              </View>
              <View style={{ marginBottom: 8 }}>
                {Object.entries({
                  Email: profileData.socialLinks?.email,
                  Phone: profileData.socialLinks?.phone,
                  LinkedIn: profileData.socialLinks?.linkedin,
                  GitHub: profileData.socialLinks?.github,
                  Website: profileData.socialLinks?.personal_web,
                  Behance: profileData.socialLinks?.behance,
                  Instagram: profileData.socialLinks?.instagram,
                  YouTube: profileData.socialLinks?.youtube
                })
                .filter(([_, value]) => value)
                .map(([label, value], index) => (
                  <Text key={index} style={{fontSize: 11, marginBottom: 4, marginLeft: 5}}>
                    • {label}: {['Email', 'Phone'].includes(label) ? value : (
                      <Link src={value} style={{ color: 'blue', textDecorationLine: 'underline' }}>
                        {cleanUrl(value)}
                      </Link>
                    )}
                  </Text>
                ))}
              </View>
            </View>
          </View>
          ):(
          <View>
            <Text style={styles.header}>{profileData?.fullname || "Fill The Data"}</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
              <Text style={styles.title}>{profileData.title}</Text>
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              {[
                ['email', profileData.socialLinks?.email, false],
                ['phone', profileData.socialLinks?.phone, true],
                ['linkedin', profileData.socialLinks?.linkedin, true],
                ['github', profileData.socialLinks?.github, true],
                ['website', profileData.socialLinks?.personal_web, true],
                ['behance', profileData.socialLinks?.behance, true],
                ['instagram', profileData.socialLinks?.instagram, true],
                ['youtube', profileData.socialLinks?.youtube, true]
              ].filter(([_, url]) => url).map(([type, url], index, arr) => (
                <View key={url} style={{ flexDirection: 'row' }}>
                  {url && (
                    <>
                      {type === 'email' || type === 'phone' ? (
                        <Text style={styles.text}>{url}</Text>
                      ) : (
                        <Link
                          src={url}
                          style={[styles.text, { color: 'blue', textDecorationLine: 'underline' }]}
                        >
                          {cleanUrl(url)}
                        </Link>
                      )}
                      <Text>{" "}</Text>{index < arr.length - 1 && <Text style={styles.text}>{" | "}</Text>}
                    </>
                  )}
                </View>
              ))}
            </View>
          </View>
          )}
          {profileData.summary && (
            <View style={styles.section}>
              <Text style={styles.subheader}>SUMMARY</Text>
              <View style={{ textAlign: 'justify'}}>
                <Text style={styles.text}>{
                  profileData.summary
                  .replace(/\n/g, ' ')  
                  .replace(/\s+/g, ' ') 
                  .trim()
                }</Text>
              </View>
            </View>
          )}
        </View>

        {/* Education Section */}
        {educationData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subheader}>EDUCATION</Text>
            {educationData.map((edu, idx) => (
              <View key={idx} style={{ marginBottom: 8, textAlign: 'justify' }}>
                <Text style={styles.institutionName}>
                  {`${edu.institution} (${getYearRange(edu.start, edu.end)}${!edu.end ? ' - Now' : ''})`}
                </Text>
                <Text style={styles.text}>
                  {edu.degree} in {edu.field} {edu.gpa && ` - GPA: ${edu.gpa}`}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Skills Section */}
        {skillData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subheader}>SKILLS</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {skillData.map((skill, idx) => (
                <Text 
                  key={idx} 
                  // style={[styles.text, { backgroundColor: '#f3f4f6', padding: '4px 8px', borderRadius: 4 }]}
                  style={styles.text}
                >
                  {skill.name}{`: ${skill.sub_skill.split(";").join(", ")}`}
                </Text>
              ))}
            </View>
          </View>
        )}

        {/* Experience Section */}
        {experienceData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subheader}>EXPERIENCE</Text>
            {experienceData.map((exp, idx) => (
              <View key={idx} style={{ marginBottom: 10, textAlign: 'justify' }}>
                <Text style={styles.companyName}>{exp.company}</Text>
                {exp.certificate ? 
                  <Text style={styles.boldText}>
                    {exp.position} {formatDateRange(exp.start, exp.end)}
                    {' ( '}
                      <Link src={exp.certificate} style={[styles.portfolioLink, { marginLeft: 8 }]}>
                        View Certificate
                      </Link>
                    {' )'}
                  </Text>
                  : <Text style={styles.boldText}>{exp.position} {formatDateRange(exp.start, exp.end)}</Text>
                }

                {exp.achievements ? (
                  exp.achievements.split(';').map((item, i) => (
                    <Text style={[styles.text, styles.listItem]} key={i}>• {item.trim()}</Text>
                  ))
                ) : (
                  <Text style={styles.text}>{exp.description.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()}</Text>
                )}
              </View>
            ))}
          </View>
        )}

        {portfolioData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subheader}>PROJECTS</Text>
            {portfolioData.map((project, idx) => (
              <View key={idx} style={{ marginBottom: 10, textAlign: 'justify' }}>
                <Text style={styles.companyName}>{project.title}</Text>
                <Text style={styles.boldText}>
                  {project.type} {project.url && (
                    <>
                    {' ( '}
                    <Link src={project.url} style={styles.portfolioLink}>
                      View Project
                    </Link>
                    {' ) '}
                    </>
                  )}
                </Text>
                {project.achievements ? 
                  project.achievements.split(';').map((item, i) => (
                    <Text key={i} style={[styles.text, styles.listItem]}>
                      • {item.trim()}
                    </Text>
                  ))
                  : project.description && (
                    <Text style={styles.text}>
                      {project.description.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()}
                    </Text>
                  )
                }
              </View>
            ))}
          </View>
        )}

        {/* Achievements Section */}
        {achievementData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subheader}>ACHIEVEMENTS</Text>
            {achievementData.map((ach, idx) => (
              <View key={idx} style={{ marginBottom: 8, textAlign: 'justify' }}>
                <Text style={styles.companyName}>{ach.name}</Text>
                {ach.certificate_link ?
                  <Text style={styles.boldText}>
                    {ach.organizer}
                    {/* {` (${ach.level}) `} */}
                    {` (${new Date(ach.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}) `}
                    {` ( `}
                      <Link src={ach.certificate_link} style={[styles.portfolioLink, { marginLeft: 8 }]}>
                        View Certificate
                      </Link>
                    {` )`}
                  </Text>
                  : <Text style={styles.text}>{ach.organizer} {formatSingleDate(`(${ach.date})`)}</Text>
                }
                <Text style={styles.text}>{ach.description.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Certificates Section */}
        {certificateData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subheader}>CERTIFICATIONS</Text>
            {certificateData.map((cert, idx) => (
              <View key={idx} style={{ marginBottom: 8, textAlign: 'justify' }}>
                <Text style={styles.companyName}>{cert.title}</Text>
                {cert.cred_url ?
                  <Text style={styles.text}>{cert.org} {cert.cred_id && cert.cred_id } {formatSingleDate(`(${cert.issue})`)}
                    {' ( '}
                      <Link src={cert.cred_url} style={[styles.portfolioLink, { marginLeft: 8 }]}>View Certificate</Link>
                    {' )'}
                  </Text>
                  : <Text style={styles.text}>{cert.org} {cert.cred_id && cert.cred_id } {formatSingleDate(`(${cert.issue})`)} </Text>
                }
              </View>
            ))}
          </View>
        )}

        {/* Publications Section */}
        {publicationData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subheader}>PUBLICATIONS</Text>
            {publicationData.map((pub, idx) => (
              <View key={idx} style={{ marginBottom: 8, textAlign: 'justify' }}>
                <Text style={styles.companyName}>{pub.title}</Text>
                  {pub.url ? 
                    (
                      <Text style={styles.text}>{pub.publisher} {formatSingleDate(`(${pub.date})`)}
                        {' ( '}
                        <Link src={pub.url} style={[styles.portfolioLink, { marginLeft: 8 }]}>View Publication</Link>
                        {' )'}
                      </Text>
                    )
                    : <Text style={styles.text}>{pub.publisher} {formatSingleDate(`(${pub.date})`)} </Text>
                  }
              </View>
            ))}
          </View>
        )}
      </Page>
    </Document>
  );
};

export default BasicAtsTemplate;